// =============================================================================
// Edict Schema Migration — Auto-migrate ASTs from older schema versions
// =============================================================================
// Migrations are auto-derived by diffing stored schema snapshots at build time.
// This module applies those migrations to incoming ASTs transparently.

import type { StructuredError } from "../errors/structured-errors.js";
import { migrationFailed, unsupportedSchemaVersion } from "../errors/structured-errors.js";

// =============================================================================
// Types
// =============================================================================

export type MigrationOp =
    | { op: "add_field"; path: string; default: unknown }
    | { op: "remove_field"; path: string }
    | { op: "rename_field"; path: string; newName: string }
    | { op: "set_field"; path: string; value: unknown };

export interface Migration {
    from: string;
    to: string;
    ops: MigrationOp[];
}

export interface MigrationSuccess {
    ok: true;
    ast: unknown;
    fromVersion: string;
    toVersion: string;
    migrationsApplied: number;
}

export interface MigrationFailure {
    ok: false;
    errors: StructuredError[];
}

export type MigrationResult = MigrationSuccess | MigrationFailure;

// =============================================================================
// Version Constants
// =============================================================================

export const CURRENT_SCHEMA_VERSION = "1.1";
export const MINIMUM_SCHEMA_VERSION = "1.0";

// =============================================================================
// Migration Registry — auto-generated + manual overrides
// =============================================================================
// The first migration establishes the versioning mechanism itself.
// Future migrations are auto-generated by scripts/diff-schemas.ts.

export const MIGRATION_REGISTRY: Migration[] = [
    {
        from: "1.0",
        to: "1.1",
        ops: [
            { op: "set_field", path: "schemaVersion", value: "1.1" },
        ],
    },
];

// =============================================================================
// Apply a single migration
// =============================================================================

/**
 * Apply a single migration's ops to an AST.
 * Returns the transformed AST (deep-cloned, original not mutated).
 */
export function applyMigration(ast: unknown, migration: Migration): MigrationResult {
    // Deep clone to avoid mutating the original
    let result: unknown;
    try {
        result = JSON.parse(JSON.stringify(ast));
    } catch {
        return {
            ok: false,
            errors: [migrationFailed(migration.from, migration.to, "Failed to clone AST")],
        };
    }

    for (const migrationOp of migration.ops) {
        try {
            applyOp(result as Record<string, unknown>, migrationOp);
        } catch (e: unknown) {
            const detail = e instanceof Error ? e.message : String(e);
            return {
                ok: false,
                errors: [migrationFailed(migration.from, migration.to, `Op "${migrationOp.op}" at "${migrationOp.path}": ${detail}`)],
            };
        }
    }

    return {
        ok: true,
        ast: result,
        fromVersion: migration.from,
        toVersion: migration.to,
        migrationsApplied: 1,
    };
}

// =============================================================================
// Migrate to latest
// =============================================================================

/**
 * Detect the schema version of an AST and apply all necessary migrations
 * to bring it to CURRENT_SCHEMA_VERSION.
 *
 * If the AST has no `schemaVersion` field, assumes "1.0".
 * Returns the migrated AST with version metadata.
 */
export function migrateToLatest(ast: unknown): MigrationResult {
    if (typeof ast !== "object" || ast === null || Array.isArray(ast)) {
        // Not an object — pass through (will fail validation later)
        return { ok: true, ast, fromVersion: "unknown", toVersion: "unknown", migrationsApplied: 0 };
    }

    const obj = ast as Record<string, unknown>;
    const inputVersion = typeof obj.schemaVersion === "string"
        ? obj.schemaVersion
        : MINIMUM_SCHEMA_VERSION;

    // Already current
    if (inputVersion === CURRENT_SCHEMA_VERSION) {
        return { ok: true, ast, fromVersion: inputVersion, toVersion: CURRENT_SCHEMA_VERSION, migrationsApplied: 0 };
    }

    // Find applicable migrations
    const applicable = findMigrationChain(inputVersion, CURRENT_SCHEMA_VERSION);
    if (applicable === null) {
        return {
            ok: false,
            errors: [unsupportedSchemaVersion(inputVersion, {
                min: MINIMUM_SCHEMA_VERSION,
                max: CURRENT_SCHEMA_VERSION,
            })],
        };
    }

    // Apply migrations sequentially
    let current: unknown = ast;
    let totalApplied = 0;

    for (const migration of applicable) {
        const result = applyMigration(current, migration);
        if (!result.ok) {
            return result;
        }
        current = result.ast;
        totalApplied++;
    }

    return {
        ok: true,
        ast: current,
        fromVersion: inputVersion,
        toVersion: CURRENT_SCHEMA_VERSION,
        migrationsApplied: totalApplied,
    };
}

// =============================================================================
// Internal helpers
// =============================================================================

/**
 * Find the ordered chain of migrations from `from` to `to`.
 * Returns null if no path exists.
 */
function findMigrationChain(from: string, to: string): Migration[] | null {
    const chain: Migration[] = [];
    let current = from;

    while (current !== to) {
        const next = MIGRATION_REGISTRY.find((m) => m.from === current);
        if (!next) return null;
        chain.push(next);
        current = next.to;
    }

    return chain;
}

/**
 * Apply a single operation to an AST object.
 * Paths use dot notation for nested access (e.g. "a.b.c").
 */
function applyOp(obj: Record<string, unknown>, op: MigrationOp): void {
    const { path } = op;
    const parts = path.split(".");
    const field = parts.pop()!;
    let target: Record<string, unknown> = obj;

    // Navigate to parent
    for (const part of parts) {
        const next = target[part];
        if (typeof next !== "object" || next === null) {
            throw new Error(`Path segment "${part}" does not resolve to an object`);
        }
        target = next as Record<string, unknown>;
    }

    switch (op.op) {
        case "add_field":
            if (!(field in target)) {
                target[field] = op.default;
            }
            break;
        case "remove_field":
            delete target[field];
            break;
        case "rename_field":
            if (field in target) {
                target[op.newName] = target[field];
                delete target[field];
            }
            break;
        case "set_field":
            target[field] = op.value;
            break;
    }
}
